import type { FetchPolicy } from '@apollo/client';
import { getLanguageFromLocale, type Locale } from '@seek/melways-sites';

import { PER_PAGE } from 'src/App/constants/filter';
import {
  type DraftJobListItemFragment,
  type DraftJobsQueryVariables,
  useDraftJobsQuery,
} from 'src/graphql/__generated__';
import { useConfig } from 'src/hooks/useConfig';
import { useQueryParam } from 'src/hooks/useQueryParam';
import { mapPageToOffset } from 'src/utils/mapPageToOffset';

export const useGetQueryDraftJobsOptions = (): {
  fetchPolicy: FetchPolicy;
  variables: DraftJobsQueryVariables;
} => {
  const { page, q } = useQueryParam();
  const { locale } = useConfig();
  const language = getLanguageFromLocale(locale as Locale);

  return {
    fetchPolicy: 'network-only',
    variables: {
      input: {
        limit: PER_PAGE,
        offset: mapPageToOffset(PER_PAGE, page),
        term: q,
        language,
      },
    },
  };
};

export const useQueryDraftJobs = () => {
  const options = useGetQueryDraftJobsOptions();

  const { loading, error, data } = useDraftJobsQuery(options);

  return {
    loading,
    error,
    totalCount:
      data?.draftJobs?.__typename === 'JobsResponseSuccess'
        ? data.draftJobs.result.totalCount
        : 0,
    draftJobs:
      data?.draftJobs?.__typename === 'JobsResponseSuccess'
        ? data.draftJobs.result.edges
            .map(({ node }) => node)
            .filter(
              (node): node is DraftJobListItemFragment =>
                node.__typename === 'DraftJob',
            )
        : [],
  };
};
